import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../../services/apiClient";

export const budgetOverview = createAsyncThunk(
  'budget/getOverveiw',
  async ({ canvasId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/canvas/${canvasId}/budgets/overview`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }

)

// Fetch budgets for a canvas (with pagination)
export const fetchBudgets = createAsyncThunk(
  'budget/fetchBudgets',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/canvas/${canvasId}/budgets?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Create a budget item
export const createBudget = createAsyncThunk(
  'budget/createBudget',
  async ({ canvasId, budgetData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasId}/budgets`, budgetData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Update a budget item
export const updateBudget = createAsyncThunk(
  'budget/updateBudget',
  async ({ canvasId, budgetId, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/canvas/${canvasId}/budgets/${budgetId}`,
        updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Delete a budget item
export const deleteBudget = createAsyncThunk(
  'budget/deleteBudget',
  async ({ canvasId, budgetId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(
        `/canvas/${canvasId}/budgets/${budgetId}`);
      return budgetId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload budgets from file
export const uploadBudgets = createAsyncThunk(
  'budget/uploadBudgets',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      if (!(file instanceof File)) {
        throw new Error(`Expected File object but got ${typeof file}: ${JSON.stringify(file)}`);
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/canvas/${canvasId}/budgets/upload`,
        formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

const budgetSlice = createSlice({
  name: 'budget',
  initialState: {
    budgets: [],
    pagination: { total: 0, page: 1, pages: 1 },
    loading: false,
    overview: null,
    error: null,
  },
  reducers: {
    resetBudgetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //get overview
     .addCase(budgetOverview.fulfilled, (state, action) => {
        state.overview = action.payload;
      })
      // Fetch
      .addCase(fetchBudgets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.loading = false;
        state.budgets = action.payload.budgets;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.budgets.push(action.payload);
      })
      .addCase(createBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBudget.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.budgets.findIndex(b => b._id === action.payload._id);
        if (idx !== -1) state.budgets[idx] = action.payload;
      })
      .addCase(updateBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.budgets = state.budgets.filter(b => b._id !== action.payload);
      })
      .addCase(deleteBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload
      .addCase(uploadBudgets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadBudgets.fulfilled, (state, action) => {
        state.loading = false;
        state.budgets = [...state.budgets, ...(action.payload?.budgets || [])];
      })
      .addCase(uploadBudgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetBudgetError } = budgetSlice.actions;
export const selectBudgets = state => state.budget.budgets;
export const selectBudgetLoading = state => state.budget.loading;
export const selectBudgetPagination = state => state.budget.pagination;
export const selectBudgetOverview = state => state.budget.overview;
export default budgetSlice.reducer;